import { createSlice, current } from '@reduxjs/toolkit';
import { graphqlApi } from '../api'
import { RUBRIC_STATUS } from '../../../../src/util/constants';

const initialState = {
  rubricKey: '',
  rubrics: { honors: { stagedItemsCopy: [] }, scholars: { stagedItemsCopy: [] } },
  gradingTemplates: {}
}

const rubricSlice = createSlice({
  name: 'rubrics',
  initialState,
  reducers: {
    updateVisibility(state, action) {
      const { rubric, item, visibility } = action.payload
      const program = rubric.programId
      const rubricKey = rubric.key
      const index = state.rubrics[program][rubricKey].items.findIndex((i) => i.label === item.label);
      state.rubrics[program][rubricKey].items[index].isEnabled = visibility
    },
    revertChanges(state, action) {
      const { rubric, originalItems } = action.payload
      const program = rubric.programId
      const rubricKey = rubric.key
      if (state.rubrics[program][rubricKey].status === RUBRIC_STATUS.STAGED) {
        state.rubrics[program][rubricKey].items = originalItems
      } else {
        console.error("Incorrect rubric.")
      }
    },
    addItem(state, action) {
      const { rubric, newItem } = action.payload
      const program = rubric.programId
      const rubricKey = rubric.key
      const currentState = current(state)
      const existingItem = currentState.rubrics[program][rubricKey]?.items?.find(e => e.label.toLowerCase() === newItem.label.toLowerCase())
      if (!existingItem) {
        newItem.order = state.rubrics[program][rubricKey].items.length + 1
        state.rubrics[program][rubricKey].items = state.rubrics[program][rubricKey].items.concat({ isEnabled: newItem.isEnabled, gradingTemplate: newItem.gradingTemplate, label: newItem.label, order: newItem.order })
      } else {
        console.error("Item already exists.")
      }
    },
    deleteItem(state, action) {
      const { rubric, newItem } = action.payload
      const program = rubric.programId
      const rubricKey = rubric.key
      state.rubrics[program][rubricKey].items = state.rubrics[program][rubricKey].items.filter((item) => item.label !== newItem.label)
    },
    changeItemOrder(state, action) {
      const { rubric, item, direction } = action.payload
      const newItems = rubric.items.map(item => ({ ...item }));
      const program = rubric.programId
      const rubricKey = rubric.key
      const index = newItems.findIndex((i) => i.label === item.label);

      if (direction === 'up' && index > 0) {
        [newItems[index], newItems[index - 1]] = [newItems[index - 1], newItems[index]];
      } else if (direction === 'down' && index < newItems.length - 1) {
        [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
      }

      // update sort order after modifying array
      newItems.forEach((item, index) => { item.order = index + 1 });

      state.rubrics[program][rubricKey].items = newItems
    },
    setRubricKey(state, action) {
      state.rubricKey = action.payload
    }
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      graphqlApi.endpoints.getRubrics.matchFulfilled,
      (state, { payload }) => {
        const activeRubric = payload.find((item) => item.status === RUBRIC_STATUS.ACTIVE)
        if (activeRubric) state.rubricKey = activeRubric.key
        payload.forEach((item) => {
          const program = item?.programId
          const rubricKey = item?.key
          if (program && rubricKey) {
            state.rubrics[program][rubricKey] = item
            if (item.status === RUBRIC_STATUS.STAGED) state.rubrics[program].stagedItemsCopy = item.items
          }
        })
      }
    )
    builder.addMatcher(
      graphqlApi.endpoints.getRubricConfig.matchFulfilled,
      (state, { payload }) => {
        const parsedPayload = JSON.parse(payload.gradingTemplates)
        state.gradingTemplates = parsedPayload
      }
    )
    builder.addMatcher(
      graphqlApi.endpoints.getRubricConfig.matchRejected,
      (state, payload) => {
      }
    )
    builder.addMatcher(
      graphqlApi.endpoints.createARubric.matchFulfilled,
      (state, { payload }) => {
        const program = payload.programId
        const rubricKey = payload.key
        state.rubrics[program][rubricKey] = payload
        state.rubricKey = rubricKey
      }
    )
    builder.addMatcher(
      graphqlApi.endpoints.createARubric.matchRejected,
      (state, { payload }) => {
      }
    )
    builder.addMatcher(
      graphqlApi.endpoints.cloneRubric.matchFulfilled,
      (state, { payload }) => {
        const program = payload.programId
        const rubricKey = payload.key
        if (program && rubricKey) {
          state.rubrics[program][rubricKey] = payload
          state.rubrics[program].stagedItemsCopy = payload.items
        }
      }
    )
    builder.addMatcher(
      graphqlApi.endpoints.saveRubricV2.matchFulfilled,
      (state, { payload }) => {
        const { programId, key } = payload
        const newItems = payload.items
        const currentState = current(state)
        const isStagedRubric = currentState.rubrics[programId][key].status === RUBRIC_STATUS.STAGED
        // specific order required
        if (isStagedRubric) state.rubrics[programId].stagedItemsCopy = newItems
        if (programId && key) state.rubrics[programId][key].updatedDate = payload.updatedDate
      }
    )
    builder.addMatcher(
      graphqlApi.endpoints.setActiveRubric.matchFulfilled,
      (state, { payload }) => {
        const program = payload[0].programId
        const newActiveRubricKey = payload.find((item) => item.status === RUBRIC_STATUS.ACTIVE).key
        const prevActiveRubricKey = payload?.find((item) => item.status === RUBRIC_STATUS.ARCHIVED)?.key
        state.rubrics[program][newActiveRubricKey].status = RUBRIC_STATUS.ACTIVE
        if (prevActiveRubricKey) { state.rubrics[program][prevActiveRubricKey].status = RUBRIC_STATUS.ARCHIVED }
        state.rubrics[program].stagedItemsCopy = []
      }
    )
    builder.addMatcher(
      graphqlApi.endpoints.archiveRubric.matchFulfilled,
      (state, { payload }) => {
        const program = payload.programId
        const rubricKey = payload.key
        if (program && rubricKey) state.rubrics[program][rubricKey].status = RUBRIC_STATUS.ARCHIVED
      }
    )
    builder.addMatcher(
      graphqlApi.endpoints.setActiveRubric.matchRejected,
      (state, { payload }) => {
      }
    )
    builder.addMatcher(
      graphqlApi.endpoints.deleteRubric.matchFulfilled,
      (state, { payload }) => {
        const program = payload.programId
        const rubricKey = payload.key
        if (program && rubricKey) { delete state.rubrics[program][rubricKey] }
        state.rubrics[program].stagedItemsCopy = []
      }
    )
  }
});

export const { updateVisibility, addItem, deleteItem, changeItemOrder, revertChanges, setRubricKey } = rubricSlice.actions;
export const selectRubricKey = (state) => state.rubricSlice.rubricKey
export const selectRubricsData = (state) => state.rubricSlice
export const selectRubrics = (state) => state.rubricSlice.rubrics
export const selectGradingTemplates = (state) => state.rubricSlice.gradingTemplates

export default rubricSlice.reducer;